import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Space,
  Breadcrumb,
  Card,
  List,
  Tag,
  Empty,
  Spin,
  Alert,
  Button,
} from 'antd';
import {
  HomeOutlined,
  SearchOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useSearch } from '../api/queries/useSearch';
import { useTenant } from '../hooks/useTenant';
import { TraceSearchInput } from '../components/search/TraceSearchInput';
import type { JobSummary } from '../api/types';
import { getErrorMessage } from '../utils/error';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const MATCH_TYPE_LABELS: Record<string, string> = {
  trace_id: 'Trace ID',
  job_id: 'Job ID',
  span_id: 'Span ID',
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  JOB_END: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
  JOB_END_EXCEPTION: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
};

export function SearchResultsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q');
  const { tenantId, isLoading: isTenantLoading } = useTenant();

  const { data, isLoading, error } = useSearch(query);

  if (isTenantLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!tenantId) {
    return (
      <Alert
        type="warning"
        message="No Tenant Selected"
        description="Please select a tenant from the header."
        showIcon
      />
    );
  }

  const renderJobItem = (job: JobSummary) => (
    <List.Item
      key={job.job_id}
      actions={[
        <Button
          type="primary"
          size="small"
          icon={<LinkOutlined />}
          onClick={() => navigate(`/jobs/${job.job_id}`)}
        >
          View Details
        </Button>,
      ]}
    >
      <List.Item.Meta
        avatar={STATUS_ICONS[job.status] || <CheckCircleOutlined />}
        title={
          <Space>
            <Text strong>{job.job_id}</Text>
            <Tag>{job.namespace}</Tag>
          </Space>
        }
        description={
          <Space direction="vertical" size={0}>
            <Text type="secondary">Trace: {job.trace_id}</Text>
            {job.start_time && (
              <Text type="secondary">
                Started: {dayjs(job.start_time).format('YYYY-MM-DD HH:mm:ss')}
              </Text>
            )}
          </Space>
        }
      />
    </List.Item>
  );

  return (
    <div>
      <Breadcrumb
        items={[
          {
            href: '/',
            title: (
              <>
                <HomeOutlined />
                <span>Dashboard</span>
              </>
            ),
          },
          {
            title: (
              <>
                <SearchOutlined />
                <span>Search</span>
              </>
            ),
          },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
          <Title level={3} style={{ margin: 0 }}>
            Search Results
          </Title>
          <TraceSearchInput />
        </Space>

        {query && (
          <Card size="small">
            <Space>
              <Text type="secondary">Searching for:</Text>
              <Tag color="blue">{query}</Tag>
              {data && (
                <>
                  <Text type="secondary">Matched as:</Text>
                  <Tag color="green">{MATCH_TYPE_LABELS[data.match_type]}</Tag>
                </>
              )}
            </Space>
          </Card>
        )}

        {error && (
          <Alert
            type="error"
            message="Search failed"
            description={getErrorMessage(error)}
            showIcon
          />
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
            <Paragraph style={{ marginTop: 16 }}>Searching...</Paragraph>
          </div>
        ) : !query ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Enter a trace ID, job ID, or span ID to search"
          />
        ) : data?.jobs.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                No results found for <Text strong>{query}</Text>
              </span>
            }
          />
        ) : (
          <Card title={`Found ${data?.jobs.length} job(s)`}>
            <List
              itemLayout="horizontal"
              dataSource={data?.jobs || []}
              renderItem={renderJobItem}
            />
          </Card>
        )}
      </Space>
    </div>
  );
}
